import type { AreaId, PersonId, SubAreaId } from '@shared/brand';

/**
 * El organigrama de un área: un dibujo, no el padrón.
 *
 * Dice quién le reporta a quién **en el papel que la empresa enseña**, y a
 * propósito no mueve nada del portal. Los vínculos de `personLeaders` —los que
 * deciden quién firma unas vacaciones— siguen siendo los únicos que firman: el
 * organigrama se puede sembrar desde ellos, pero no los escribe. Mover una línea
 * en un lienzo no puede cambiar quién aprueba las vacaciones de treinta personas
 * sin que nadie lo note. Si algún día tiene que hacerlo, es una decisión aparte y
 * con su propia confirmación.
 */

/**
 * Lo que puede haber en el lienzo.
 *
 * - `persona`: alguien del padrón. Su nombre y su puesto salen de su ficha, así
 *   que un cambio de nombre no deja el dibujo viejo.
 * - `vacante`: un puesto sin nadie. Es lo que se dibuja primero cuando se diseña
 *   una estructura, y lo que se ocupa soltando a una persona encima.
 * - `equipo`: una cabecera de grupo —«Desarrollo iOS»—, casi siempre una sub
 *   área, que agrupa a quienes le reportan.
 * - `nota`: un comentario sobre el dibujo. No lleva líneas.
 */
export const ORG_NODE_KINDS = ['persona', 'vacante', 'equipo', 'nota'] as const;
export type OrgNodeKind = (typeof ORG_NODE_KINDS)[number];

export const isOrgNodeKind = (value: unknown): value is OrgNodeKind =>
  typeof value === 'string' && (ORG_NODE_KINDS as readonly string[]).includes(value);

export interface OrgNode {
  readonly id: string;
  readonly kind: OrgNodeKind;
  /** Esquina superior izquierda, en unidades del lienzo. */
  readonly x: number;
  readonly y: number;
  /** De quién es la tarjeta. Sólo en `persona`; `null` en las demás. */
  readonly personId: PersonId | null;
  /** Qué sub área representa un `equipo`, cuando representa una. */
  readonly subAreaId: SubAreaId | null;
  /**
   * El texto escrito a mano, y significa algo distinto en cada tipo:
   *
   * - `vacante`: el puesto que falta cubrir.
   * - `equipo`: su nombre. Vacío en uno ligado a una sub área = el de la sub área.
   * - `nota`: la nota.
   * - `persona`: vacío = el puesto de su ficha. Escrito, lo sustituye **sólo en
   *   el dibujo**: «Líder técnico de la app» dice más que el puesto de nómina, y
   *   no por eso hay que cambiarle el puesto a nadie.
   */
  readonly label: string;
}

/**
 * Las tres líneas que sabe dibujar un organigrama.
 *
 * - `reporta`: `from` dirige a `to`. **Una sola por tarjeta**: es el jefe
 *   directo, y es la que ordena el dibujo en niveles.
 * - `secundaria`: la línea punteada — `to` también le responde a `from`, por un
 *   proyecto. Es la firma doble del portal (una persona con dos líderes) dicha en
 *   el idioma de los organigramas.
 * - `par`: mismo nivel, sin dirección. Para decir «estos dos están a la par»
 *   cuando no comparten jefe en el dibujo.
 */
export const ORG_LINK_KINDS = ['reporta', 'secundaria', 'par'] as const;
export type OrgLinkKind = (typeof ORG_LINK_KINDS)[number];

export const isOrgLinkKind = (value: unknown): value is OrgLinkKind =>
  typeof value === 'string' && (ORG_LINK_KINDS as readonly string[]).includes(value);

export interface OrgLink {
  readonly id: string;
  readonly kind: OrgLinkKind;
  readonly from: string;
  readonly to: string;
}

/** Lo que se dibuja: tarjetas y líneas. Es lo que se guarda y lo que se deshace. */
export interface OrgChartBody {
  readonly nodes: readonly OrgNode[];
  readonly links: readonly OrgLink[];
}

export interface OrgChart extends OrgChartBody {
  readonly areaId: AreaId;
  /** `null` en un organigrama que todavía no se ha guardado nunca. */
  readonly updatedAt: Date | null;
  /** El nombre de quien lo guardó por última vez, para decirlo en la cabecera. */
  readonly updatedByName: string | null;
}

export const EMPTY_ORG_CHART_BODY: OrgChartBody = { nodes: [], links: [] };

/**
 * Cuántas tarjetas caben en un organigrama.
 *
 * El área más grande tiene 137 personas; esto deja sitio para cuatro veces eso,
 * más vacantes y notas, y sigue lejos del megabyte que admite un documento.
 */
export const ORG_CHART_MAX_NODES = 600;

/** Lo más largo que se puede escribir en una tarjeta o una nota. */
export const ORG_LABEL_MAX = 280;
